import { useEffect, useState, useCallback, useRef } from "react";
import { EDGE_FUNCTIONS, getEdgeFunctionHeaders } from "@/utils/edgeFunctionUrl";
import { broadcastStorageSync } from "@/hooks/useAdminStorage";

export type ContentLoadState = "loaded" | "error";

interface UseSiteContentResult {
  loadState: ContentLoadState;
  lastUpdated: string | null;
  syncFromSupabase: () => Promise<{ success: boolean; count: number; error?: string }>;
}

// ─── Cache Version Management ──────────────────────────────────────────────

const CACHE_VERSION_KEY = "ma_content_version";
const LAST_SYNC_KEY = "ma_last_supabase_sync";
const REVALIDATE_MS = 60_000; // Check version every 60s while tab is active

function getCachedVersion(): string | null {
  try {
    return localStorage.getItem(CACHE_VERSION_KEY);
  } catch { /* ignore */ }
  return null;
}

function setCachedVersion(version: string | null): void {
  try {
    if (version) {
      localStorage.setItem(CACHE_VERSION_KEY, version);
    } else {
      localStorage.removeItem(CACHE_VERSION_KEY);
    }
  } catch { /* ignore */ }
}

function getLastSyncTime(): number {
  try {
    const raw = localStorage.getItem(LAST_SYNC_KEY);
    if (raw) return parseInt(raw, 10);
  } catch { /* ignore */ }
  return 0;
}

function setLastSyncTime(): void {
  try {
    localStorage.setItem(LAST_SYNC_KEY, String(Date.now()));
  } catch { /* ignore */ }
}

/**
 * Hydrates localStorage from Supabase content map.
 * Force mode: overwrites all keys unconditionally.
 */
function hydrateLocalStorageForce(content: Record<string, unknown>): number {
  let count = 0;

  Object.entries(content).forEach(([key, value]) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      count++;
    } catch { /* ignore storage quota errors */ }
  });

  const everSavedKeys = Object.keys(content);
  if (everSavedKeys.length > 0) {
    try {
      const existing = JSON.parse(localStorage.getItem("ma_ever_saved_keys") || "[]") as string[];
      const merged = Array.from(new Set([...existing, ...everSavedKeys]));
      localStorage.setItem("ma_ever_saved_keys", JSON.stringify(merged));
    } catch { /* ignore */ }
  }

  return count;
}

function isFunctionNotFoundError(err: unknown): boolean {
  if (err instanceof TypeError) {
    const msg = err.message.toLowerCase();
    return (
      msg.includes("networkerror") ||
      msg.includes("failed to fetch") ||
      msg.includes("cors") ||
      msg.includes("network")
    );
  }
  return false;
}

/**
 * Lightweight version check: returns true if content changed on server.
 * Returns false if cache is still valid (unchanged).
 * Throws on network/edge-function errors.
 */
async function checkVersionChanged(clientVersion: string): Promise<boolean> {
  const url = new URL(EDGE_FUNCTIONS.getSiteContent);
  url.searchParams.set("version", clientVersion);
  url.searchParams.set("_cb", String(Date.now()));

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: getEdgeFunctionHeaders(),
    cache: "no-store",
  });

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`HTTP ${res.status}: ${text}`);
  }

  const json = await res.json() as {
    unchanged?: boolean;
    version?: string | null;
    content?: Record<string, unknown>;
    error?: string;
  };

  if (json.error) {
    throw new Error(json.error);
  }

  if (json.unchanged === true) {
    console.log("[useSiteContent] Version check: unchanged");
    return false;
  }

  // Server returned full content (cache miss or first visit)
  if (json.content) {
    console.log("[useSiteContent] Version check: content changed, hydrating...");
    const count = hydrateLocalStorageForce(json.content);
    setLastSyncTime();
    setCachedVersion(json.version ?? null);
    if (count > 0) {
      broadcastStorageSync();
    }
  }

  return true;
}

/**
 * Full content fetch (for first visits or forced refresh).
 */
async function fetchFullContent(): Promise<{
  content: Record<string, unknown>;
  version: string | null;
  lastUpdated: string | null;
}> {
  const url = new URL(EDGE_FUNCTIONS.getSiteContent);
  url.searchParams.set("_cb", String(Date.now()));

  let res: Response;
  try {
    res = await fetch(url.toString(), {
      method: "GET",
      headers: getEdgeFunctionHeaders(),
      cache: "no-store",
    });
  } catch (err) {
    if (isFunctionNotFoundError(err)) {
      throw new Error(
        "Edge function not found. The get-site-content function has not been deployed to your Supabase project yet."
      );
    }
    throw err;
  }

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`HTTP ${res.status}: ${text}`);
  }

  const json = await res.json() as {
    content?: Record<string, unknown>;
    version?: string | null;
    lastUpdated?: string | null;
    unchanged?: boolean;
    error?: string;
  };

  if (json.error) {
    throw new Error(json.error);
  }

  // Should never get unchanged=true here since we didn't send a version, but handle gracefully
  const content = json.content ?? {};
  const version = json.version ?? json.lastUpdated ?? null;

  return { content, version, lastUpdated: json.lastUpdated ?? null };
}

/**
 * Smart content sync:
 *  - ALWAYS renders instantly from localStorage (no loading UI)
 *  - First visit: fetches full content in background
 *  - Return visit: lightweight version ping (50 bytes), full fetch only if changed
 *  - Background revalidation every 60s while tab is active
 */
export function useSiteContent(): UseSiteContentResult {
  const [loadState, setLoadState] = useState<ContentLoadState>("loaded");
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const hasRunInit = useRef(false);

  const doFullFetch = useCallback(async () => {
    console.log("[useSiteContent] Fetching full content...");
    const { content, version, lastUpdated: lu } = await fetchFullContent();
    const contentKeys = Object.keys(content);
    if (contentKeys.length === 0) {
      console.warn(
        "[useSiteContent] Supabase returned empty content (0 keys). If you expected saved data, go to admin mode and click Save."
      );
      // Clear cached version so next load tries again
      setCachedVersion(null);
    }
    const count = hydrateLocalStorageForce(content);
    setLastSyncTime();
    setCachedVersion(version);
    if (count > 0) {
      setLastUpdated(lu);
      broadcastStorageSync();
    }
    console.log(`[useSiteContent] Full fetch complete: ${count} keys hydrated`);
  }, []);

  const doVersionCheck = useCallback(async () => {
    const cachedVersion = getCachedVersion();
    if (!cachedVersion) {
      // No cache — do full fetch
      await doFullFetch();
      return;
    }

    try {
      const changed = await checkVersionChanged(cachedVersion);
      if (changed) {
        // Content changed — already hydrated by checkVersionChanged, just update state
        setLastUpdated(getCachedVersion());
      } else {
        // Unchanged — ensure our sync time is recent so we don't aggressively re-fetch
        setLastSyncTime();
      }
    } catch (err) {
      console.warn("[useSiteContent] Version check failed, will retry on next cycle:", err);
      // Don't throw — keep showing cached content
    }
  }, [doFullFetch]);

  useEffect(() => {
    if (hasRunInit.current) return;
    hasRunInit.current = true;

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    async function init() {
      try {
        await doVersionCheck();
      } catch (err) {
        if (cancelled) return;
        console.warn("[useSiteContent] Init failed:", err);
        setLoadState("error");
      }

      // Background revalidation: check version every 60s while tab is active
      intervalId = setInterval(() => {
        if (document.hidden) return;
        doVersionCheck().catch((e) => {
          console.warn("[useSiteContent] Background revalidation failed:", e);
        });
      }, REVALIDATE_MS);
    }

    init();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [doVersionCheck]);

  const syncFromSupabase = useCallback(async (): Promise<{
    success: boolean;
    count: number;
    error?: string;
  }> => {
    try {
      const { content, version, lastUpdated: lu } = await fetchFullContent();
      const count = hydrateLocalStorageForce(content);
      setLastSyncTime();
      setCachedVersion(version);
      if (count > 0) {
        setLastUpdated(lu);
        broadcastStorageSync();
      }
      return { success: true, count };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { success: false, count: 0, error: msg };
    }
  }, []);

  return { loadState, lastUpdated, syncFromSupabase };
}

export async function saveSiteContentToSupabase(
  updates: Record<string, unknown>,
  adminPassword: string
): Promise<{ success: boolean; count?: number; savedKeys?: string[]; error?: string; version?: string | null }> {
  try {
    let res: Response;
    try {
      res = await fetch(EDGE_FUNCTIONS.saveSiteContent, {
        method: "POST",
        headers: {
          ...getEdgeFunctionHeaders(),
          "x-admin-token": adminPassword,
        },
        body: JSON.stringify({ updates }),
        cache: "no-store",
      });
    } catch (err) {
      if (isFunctionNotFoundError(err)) {
        return {
          success: false,
          error:
            "Edge function not found. The save-site-content function has not been deployed to your Supabase project yet.",
        };
      }
      throw err;
    }

    const json = await res.json() as {
      success?: boolean;
      count?: number;
      saved?: string[];
      error?: string;
      version?: string | null;
    };

    if (!res.ok || !json.success) {
      return { success: false, error: json.error || `HTTP ${res.status}` };
    }

    // Update cached version immediately so the next page load doesn't re-fetch
    if (json.version) {
      setCachedVersion(json.version);
    }

    return { success: true, count: json.count, savedKeys: json.saved, version: json.version };
  } catch (err) {
    return { success: false, error: String(err) };
  }
}